import React, { useState, useEffect, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import { RadioGroup, Radio, FormControlLabel, styled } from "@mui/material";
import * as S from "./styles";
import DeleteIcon from "assets/images/settings/delete.svg";
import CloseIcon from "@mui/icons-material/Close";
import Button from "../Button";
import AddPayment from "../Settings/AddPayment";
import { getAllCards } from "../../services/api";
import { useRemoveCard, useSetDefaultCard } from "../../services/mutations";
import { isCardExpired } from "../../utils/helpers";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import { colors } from "../../utils";
import { getCardIcon } from "../Settings/Billing/helper";
import { planCardDeletion } from "./helpers";

const StyledRadio = styled(Radio)(() => ({
  "&.Mui-checked": {
    color: "#9B5AF8",
  },
}));

const PaymentMethods = ({
  open,
  handleClose,
  forcePopupClose,
  onCardChanged,
}) => {
  const [addNewStep, setAddNewStep] = useState(false);
  const [cards, setCards] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [deletingCardId, setDeletingCardId] = useState(null);
  const [cardPendingDelete, setCardPendingDelete] = useState(null);
  const [loadingCardId, setLoadingCardId] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(null);

  const removeCard = useRemoveCard();
  const setDefault = useSetDefaultCard();

  const fetchCards = useCallback(async () => {
    setFetching(true);
    const response = await getAllCards();
    if (!response.data.cards?.length) {
      setFetching(false);
      setAddNewStep(true);
    }
    const cardsData = response.data.cards || [];
    setCards(cardsData);
    // Set the default card as selected, or first card if no default
    const defaultCard = cardsData.find((card) => card.is_default);
    setSelectedCardId(
      defaultCard
        ? defaultCard.id
        : cardsData.length > 0
        ? cardsData[0].id
        : null
    );
    setFetching(false);
    // Notify parent component about card changes
    onCardChanged && onCardChanged(cardsData);
  }, [onCardChanged]);

  useEffect(() => {
    fetchCards();
  }, []);

  // Open the confirmation dialog for a card (destructive, irreversible action).
  const requestDelete = (card) => {
    setCardPendingDelete(card);
  };

  // Run the delete after the user confirms.
  const confirmDelete = () => {
    const card = cardPendingDelete;
    setCardPendingDelete(null);
    if (card) {
      handleCardDelete(card.id);
    }
  };

  const handleCardDelete = async (cardId) => {
    setDeletingCardId(cardId);
    removeCard.mutateAsync(
      {
        pm_id: cardId,
      },
      {
        onSuccess: async () => {
          // Decide what remains and whether the server default must change.
          const { remaining, newDefaultId } = planCardDeletion(
            cards,
            cardId,
            isCardExpired
          );

          // Reflect the new default locally so the UI is immediately correct.
          const nextCards = newDefaultId
            ? remaining.map((card) => ({
                ...card,
                is_default: card.id === newDefaultId,
              }))
            : remaining;
          setCards(nextCards);

          // Keep the radio selection valid.
          if (selectedCardId === cardId) {
            setSelectedCardId(
              newDefaultId
                ? newDefaultId
                : nextCards.length > 0
                ? nextCards[0].id
                : null
            );
          }

          // Notify parent about card changes (incl. all cards deleted).
          onCardChanged && onCardChanged(nextCards);
          setDeletingCardId(null);

          // The deleted card WAS the default: tell the SERVER the new default,
          // otherwise Stripe is left with no default payment method.
          if (newDefaultId) {
            try {
              await setDefault.mutateAsync({ payment_method: newDefaultId });
            } catch (err) {
              // If the server refused, re-sync from server truth.
              console.log({ err });
              fetchCards();
            }
          }
        },
        onError: (err) => {
          setDeletingCardId(null);
          toast.error("Could not remove this card. Please try again.");
          console.log({ err });
        },
      }
    );
  };

  const setDefaultCard = () => {
    if (!selectedCardId) return;

    setLoadingCardId(selectedCardId);
    setDefault.mutateAsync(
      {
        payment_method: selectedCardId,
      },
      {
        onSuccess: (res) => {
          toast.success("Card set as default.");
          const filtered = cards.map((card) => {
            return {
              ...card,
              is_default: card.id === selectedCardId,
            };
          });
          setCards(filtered);
          setLoadingCardId(null);
          onCardChanged && onCardChanged(filtered);
        },
        onError: (err) => {
          console.log(err);
          setLoadingCardId(null);
        },
      }
    );
  };

  const handleRadioChange = (event) => {
    setSelectedCardId(event.target.value);
  };

  return (
    <>
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <S.DialogContainer>
        <S.StyledDialogTitle>
          <S.Title>Payment Methods</S.Title>
          <CloseIcon className="close-icon" onClick={handleClose} />
        </S.StyledDialogTitle>

        <S.Separator />
        {fetching && (
          <S.LoadingContainer>
            <CircularProgress />
          </S.LoadingContainer>
        )}
        {!fetching && (
          <S.StyledDialogContent>
            {!addNewStep ? (
              cards.length ? (
                <RadioGroup
                  value={selectedCardId}
                  onChange={handleRadioChange}
                  style={{ width: "100%" }}
                >
                  {cards?.map((card) => {
                    const isExpired = isCardExpired(
                      card.expiry_month,
                      card.expiry_year
                    );
                    return (
                      <S.CardItem
                        isDefaultCard={card.is_default}
                        key={card.id}
                        style={{ marginBottom: "16px" }}
                      >
                        <S.PaymentMethodItem>
                          <FormControlLabel
                            value={card.id}
                            control={
                              <StyledRadio
                                checked={selectedCardId === card.id}
                              />
                            }
                            label={
                              <S.CardDetails>
                                <S.CardLogo
                                  src={getCardIcon(card?.brand)}
                                  alt={card.brand}
                                />
                                <S.CardInfo>
                                  <S.CardNumber>
                                    •••• •••• •••• {card.last4}
                                  </S.CardNumber>
                                  <S.CardExpiry>
                                    Exp. date {card.expiry_month}/
                                    {card.expiry_year}
                                  </S.CardExpiry>
                                </S.CardInfo>
                              </S.CardDetails>
                            }
                          />
                          <S.ButtonsContainer>
                            {isExpired ? (
                              <S.ExpireButton>Expired</S.ExpireButton>
                            ) : (
                              card.is_default && (
                                <S.DefaultButton>Default</S.DefaultButton>
                              )
                            )}
                            {deletingCardId === card.id ? (
                              <CircularProgress
                                size={20}
                                style={{ marginLeft: "5px" }}
                              />
                            ) : (
                              <S.DeleteButton
                                type="button"
                                aria-label={`Remove card ending in ${card.last4}`}
                                disabled={deletingCardId !== null}
                                onClick={() => requestDelete(card)}
                              >
                                <img
                                  src={DeleteIcon}
                                  alt=""
                                  width={24}
                                  height={24}
                                />
                              </S.DeleteButton>
                            )}
                          </S.ButtonsContainer>
                        </S.PaymentMethodItem>
                      </S.CardItem>
                    );
                  })}
                </RadioGroup>
              ) : (
                <S.InternalHeading>
                  Please add a new payment method.
                </S.InternalHeading>
              )
            ) : (
              <AddPayment
                closeModal={() => {
                  setAddNewStep(false);
                  if (forcePopupClose || cards.length === 0) {
                    handleClose();
                  } else {
                    fetchCards();
                  }
                }}
              />
            )}

            <S.BottomSeparator>
              <S.Separator />
            </S.BottomSeparator>

            {!addNewStep && (
              <S.Footer>
                <Button
                  variant={"outlined"}
                  onClick={() => setAddNewStep(true)}
                  style={{
                    border: `1px solid ${colors.PrimaryPurple600}`,
                    width: "auto",
                    whiteSpace: "nowrap",
                  }}
                >
                  New Payment Method
                </Button>
                {cards.length > 0 && (
                  <Button
                    variant={"primary"}
                    onClick={setDefaultCard}
                    disabled={!selectedCardId || loadingCardId !== null}
                    style={{
                      border: `1px solid ${colors.PrimaryPurple600}`,
                      marginLeft: "10px",
                      width: "auto",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {loadingCardId !== null ? (
                      <CircularProgress
                        size={20}
                        style={{ color: colors.PrimaryPurple600 }}
                      />
                    ) : (
                      "Set as Default"
                    )}
                  </Button>
                )}
              </S.Footer>
            )}
          </S.StyledDialogContent>
        )}
      </S.DialogContainer>
    </Dialog>

    <Dialog
      open={Boolean(cardPendingDelete)}
      onClose={() => setCardPendingDelete(null)}
      maxWidth="xs"
      fullWidth
    >
      <S.ConfirmContainer>
        <S.ConfirmTitle>Remove this card?</S.ConfirmTitle>
        <S.ConfirmText>
          The card ending in{" "}
          <S.ConfirmCardNumber>
            {cardPendingDelete?.last4}
          </S.ConfirmCardNumber>{" "}
          will be permanently removed from your account.
          {cardPendingDelete?.is_default
            ? " It’s your default card — your next card will become the default."
            : ""}{" "}
          This can’t be undone.
        </S.ConfirmText>
        <S.ConfirmFooter>
          <Button
            variant={"outlined"}
            onClick={() => setCardPendingDelete(null)}
            style={{
              width: "auto",
              padding: "0 20px",
              border: `1px solid ${colors.PrimaryPurple600}`,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            style={{ width: "auto", padding: "0 20px" }}
          >
            Remove Card
          </Button>
        </S.ConfirmFooter>
      </S.ConfirmContainer>
    </Dialog>
    </>
  );
};

export default PaymentMethods;
